(function(){
  /**
   * Component for selecting an AST level
   *
   * This lists all of the levels in an AST along with current completion for a given student.  The onSelect callback
   * is invoked when user selects a level by clicking.
   *
   * A default selection is selected from the following
   *  1. Try getting the level set in "standard" state param
   *  2. Try finding a level which corresponds to the students grade
   *  3. Find the level with the lowest ordinal (among those loaded)
   */
  angular.module('edxComponents')
    .component('edxLevelSelect', {
      templateUrl: '/resources/components/standards-completion/level-select.component/level-select.component.html',
      controller: [
        'edxStandardsCompletionService',
        '$state',
        'edxStandardsStateParamParser',
        'dblAnalyticsProvider',
        '$filter',
        '$q',
        'studentApi',
        LevelSelectController],
      controllerAs: 'vm',
      bindings: {
        districtId: '<edxDistrictId',
        userId: '<edxUserId',
        astName: '<edxAstName',
        onSelect: '&edxOnSelect',
        analyticCategory: '@edxAnalyticCategory'
      }
    });

  function LevelSelectController(
    standardsCompletionService,
    $state,
    standardsStateParamParser,
    analyticsService,
    $filter,
    $q,
    studentApi) {
    var vm = this;

    vm.$onChanges = $onChanges;
    vm.getCellWidth = getCellWidth;
    vm.getMinWidth = getMinWidth;
    vm.onClick = onClick;

    var MIN_CELL_WIDTH = 106;

    // Save student information about student
    var student;

    vm.loading = false; // This will be true while data is being loaded
    vm.levels = []; // List of LevelCompletions available for selection
    vm.selected = undefined; // Code of the currently selected level
    vm.MIN_CELL_WIDTH = MIN_CELL_WIDTH;

    if (!vm.analyticCategory) {
      vm.analyticCategory = 'Student Standards';
    }

    function $onChanges(changes) {
      if (changes.districtId || changes.userId || changes.astName) {
        _refreshLevels();
      }
    }

    /**
     * Calculate how wide each cell should be as percent of total width
     */
    function getCellWidth() {
      var totalColumns = vm.levels.length;
      var percent = Math.floor(100.0 * (100.0 / totalColumns)) / 100.0;
      return percent + "%";
    }

    /**
     * Determine the minimum width needed for all of the level cells.
     *
     * @return {number} Width in pixels
     */
    function getMinWidth() {
      return vm.levels.length * MIN_CELL_WIDTH;
    }

    /**
     * Handler for click event on a level cell
     */
    function onClick(code) {
      if (vm.selected === code) {
        return;
      }
      _setLevelSelected(code);

      analyticsService.eventTrack('Level Clicked', {
        category: vm.analyticCategory,
        label: code
      });
    }

    /**
     * Select a level given its code.
     *
     * @param code Level code
     * @private
     */
    function _setLevelSelected(code) {
      if (vm.selected === code) {
        return;
      }
      vm.selected = code;

      if (_.isFunction(vm.onSelect)) {
        vm.onSelect({code: code});
      }

      var index = _.findIndex(vm.levels, function (level){ return level.code === code; });
      if (vm.hscroll) {
        var left = index * MIN_CELL_WIDTH;
        var right = (index + 1) * MIN_CELL_WIDTH;
        var delay = vm.hscroll.getContainerWidth() === 0;
        vm.hscroll.scrollTo(left, right, delay);
      }
    }

    /**
     * Find and select a default level selection
     */
    function _makeDefaultSelection() {
      var best = _getLevelFromStandardsStateParam();
      if (!best) {
        best = _getLevelFromStudentGrade();
      }
      if (!best) {
        best = _getLowestLevelAvailable();
      }
      if (best) {
        _setLevelSelected(best);
        analyticsService.eventTrack('Level Default', {
          category: vm.analyticCategory,
          label: best
        });
      }
    }

    /**
     * Helper for _makeDefaultSelection()
     *
     * Get level from "standards" state parameter, if any.
     */
    function _getLevelFromStandardsStateParam() {
      return standardsStateParamParser.extractLevel($state.params.standards);
    }

    /**
     * Helper for _makeDefaultSelection()
     *
     * Get level from student grade, if any.
     *
     * @return {*}
     * @private
     */
    function _getLevelFromStudentGrade() {
      if (student && student.grade) {
        var level = _.reduce(vm.levels, function(best, level) {
          // Assume that all levels have a numeric grade attribute
          // If that isn't true then the outcome is not well defined, but we won't crash
          var distBest = best.grade - student.grade;
          var distLevel = level.grade - student.grade;

          if (_.isNumber(distBest) && _.isNumber(distLevel)) {
            // Pick the level with grade which is closest to student's grade
            // If both are equally distant, choose the one with a lower grade value
            if (Math.abs(distLevel) < Math.abs(distBest)) {
              return level;
            } else if (Math.abs(distLevel) > Math.abs(distBest)) {
              return best;
            } else if (distLevel < distBest) {
              return level;
            } else {
              return best;
            }
          } else {
            return best;
          }
        });
        return level.code;
      }

      return undefined;
    }

    /**
     * Helper for _makeDefaultSelection()
     *
     * Get lowest available level by ordinal.
     *
     * @return {undefined}
     * @private
     */
    function _getLowestLevelAvailable() {
      var min = _.reduce(vm.levels, function (minOrdinal, level){
        if (!minOrdinal) {
          return level;
        } else if (minOrdinal.ordinal < level.ordinal) {
          return minOrdinal;
        } else {
          return level;
        }
      });
      if (min) {
        return min.code;
      } else {
        return undefined;
      }
    }

    /**
     * Reloads level completions and re-selects default level.
     * @private
     */
    function _refreshLevels() {
      if (vm.districtId && vm.userId && vm.astName) {
        vm.loading = true;
        $q.all([
          _loadStudent(),
          standardsCompletionService.getLevelCompletionsForStudent(vm.districtId, vm.userId, vm.astName)
        ]).then(function(results){
          return results[1];
        }).then(function (levels) {
          vm.levels = $filter('orderBy')(levels, 'ordinal');
          _makeDefaultSelection();
        }).catch(function () {
          vm.levels = [];
        }).finally(function () {
          vm.loading = false;
        });
      } else {
        vm.levels = [];
      }
    }

    /**
     * Load student if needed.
     *
     * @return {*}
     * @private
     */
    function _loadStudent() {
      if (student && Number(student.id) === Number(vm.userId)) {
        return $q.resolve();
      } else if (Number(vm.districtId) !== -1) {
        return studentApi.getStudentWithPromise(vm.userId, vm.districtId).then(function(data){
          student = data;
        });
      } else {
        // getStudentWithPromise() doesn't work for home only users!
        return studentApi.getStudentDataForGuardian(vm.userId, vm.districtId).then(function(data){
          student = {
            id: data.id,
            grade: Number(data.grades)
          };
        });
      }
    }
  }
})();